import { t, uiError, LanguageSelector } from '../i18n';
import { getWebappApiUrl, webappFetch } from '../utils/apiConfig';
import React, { useEffect, useState } from 'react';
import {
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  CardContent,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Login as LoginIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';

function Login() {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const previousBodyBackground = document.body.style.backgroundColor;
    const previousHtmlBackground = document.documentElement.style.backgroundColor;
    document.body.style.backgroundColor = '#020817';
    document.documentElement.style.backgroundColor = '#020817';
    return () => {
      document.body.style.backgroundColor = previousBodyBackground;
      document.documentElement.style.backgroundColor = previousHtmlBackground;
    };
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    
    if (!formData.username || !formData.password) {
      setError('사용자명과 비밀번호를 모두 입력해주세요');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await webappFetch(`${getWebappApiUrl()}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password
        })
      });
      const body = await response.json();

      if (response.ok && body.success) {
        try {
          await webappFetch(`${getWebappApiUrl()}/api/auth/login-log`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              screenResolution: `${window.screen.width}x${window.screen.height}`,
              platform: navigator.userAgentData ? navigator.userAgentData.platform : navigator.platform,
              language: navigator.language
            })
          });
        } catch (e) {}

        localStorage.setItem('socfai_lastActivity', Date.now().toString());
        window.location.href = '/dashboard';
      } else {
        setError(body.error || 'Invalid username or password');
      }
    } catch (error) {
      setError('로그인 처리 중 오류가 발생했습니다');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        height: '100vh',
        width: '100vw',
        boxSizing: 'border-box',
        position: 'fixed',
        inset: 0,
        backgroundImage: `
          linear-gradient(90deg, rgba(2, 6, 14, 0.48) 0%, rgba(2, 6, 14, 0.34) 42%, rgba(2, 6, 14, 0.18) 100%),
          url('/images/login-port-bg.png')
        `,
        backgroundColor: '#020817',
        backgroundSize: '100% 100%, cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center, center center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, md: 5 },
        overflowX: 'hidden',
        overflowY: 'auto',
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.14) 100%)',
          pointerEvents: 'none'
        }
      }}
    >
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          minHeight: { xs: 'calc(100vh - 32px)', md: 'min(760px, calc(100vh - 80px))' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Paper 
          elevation={12} 
          sx={{ 
            width: { xs: 342, sm: 380, md: 400, lg: 430 },
            maxWidth: 'calc(100vw - 48px)',
            minWidth: 0,
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 2,
            borderRadius: 2,
            overflow: 'hidden',
            background: 'linear-gradient(145deg, rgba(15, 23, 42, 0.62), rgba(15, 23, 42, 0.34))',
            border: '1px solid rgba(226, 232, 240, 0.28)',
            boxShadow: '0 28px 72px rgba(0, 0, 0, 0.42), inset 0 1px 0 rgba(255, 255, 255, 0.22)',
            backdropFilter: 'blur(22px) saturate(1.22)',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'radial-gradient(circle at 18% 0%, rgba(255, 255, 255, 0.26), transparent 34%), linear-gradient(180deg, rgba(255,255,255,0.12), rgba(255,255,255,0.02))',
              pointerEvents: 'none'
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: '1px',
              borderRadius: '7px',
              border: '1px solid rgba(255,255,255,0.08)',
              pointerEvents: 'none'
            }
          }}
        >
          {/* 로그인 폼 섹션 */}
          <CardContent sx={{ p: { xs: 3, sm: 4 }, width: '100%', boxSizing: 'border-box', position: 'relative', zIndex: 1 }}>
            <Box sx={{ mb: 3.5 }}>
              <Typography
                component="p"
                sx={{
                  fontSize: 12,
                  color: 'rgba(147, 197, 253, 0.96)',
                  fontWeight: 800,
                  letterSpacing: 1.8,
                  textTransform: 'uppercase',
                  mb: 1
                }}
              >{t("SOCFAI")}</Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  color: '#f8fafc',
                  fontWeight: 800,
                  letterSpacing: 0
                }}
              >{t("Blockchain WebApp")}</Typography>
              <Typography
                sx={{
                  mt: 0.8,
                  color: 'rgba(226, 232, 240, 0.74)',
                  fontSize: 14,
                  lineHeight: 1.6,
                  overflowWrap: 'anywhere'
                }}
              >{t("Record · Track · Verify")}</Typography>
            </Box>

            <Box sx={{ mb: 2.5 }}>
              <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 800 }}>{t("Sign in")}</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(226, 232, 240, 0.7)', mt: 0.4 }}>{t("Enter your account credentials.")}</Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {uiError(error)}
              </Alert>
            )}

            <form noValidate onSubmit={handleSubmit}>
              <TextField
                fullWidth
                placeholder={t("UserID")}
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
                inputProps={{ 'aria-label': 'UserID' }}
                sx={{
                  mb: 2.5,
                  '& .MuiOutlinedInput-root': {
                    color: '#f8fafc',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 1,
                    backdropFilter: 'blur(10px)',
                    '& fieldset': { borderColor: 'rgba(226, 232, 240, 0.28)' },
                    '&:hover fieldset': { borderColor: 'rgba(147, 197, 253, 0.68)' },
                    '&.Mui-focused fieldset': { borderColor: '#93c5fd' }
                  },
                  '& input::placeholder': {
                    color: 'rgba(226, 232, 240, 0.78)',
                    opacity: 1
                  }
                }}
                variant="outlined"
              />

              <TextField
                fullWidth
                placeholder={t("Password")}
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleInputChange}
                required
                inputProps={{ 'aria-label': 'Password' }}
                sx={{
                  mb: 3.5,
                  '& .MuiOutlinedInput-root': {
                    color: '#f8fafc',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 1,
                    backdropFilter: 'blur(10px)',
                    '& fieldset': { borderColor: 'rgba(226, 232, 240, 0.28)' },
                    '&:hover fieldset': { borderColor: 'rgba(147, 197, 253, 0.68)' },
                    '&.Mui-focused fieldset': { borderColor: '#93c5fd' }
                  },
                  '& input::placeholder': {
                    color: 'rgba(226, 232, 240, 0.78)',
                    opacity: 1
                  }
                }}
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleTogglePassword} edge="end" sx={{ color: 'rgba(226, 232, 240, 0.78)' }}>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.96) 0%, rgba(30, 64, 175, 0.96) 100%)',
                  border: '1px solid rgba(191, 219, 254, 0.24)',
                  boxShadow: '0 16px 34px rgba(37, 99, 235, 0.32)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.98) 0%, rgba(29, 78, 216, 0.98) 100%)',
                    boxShadow: '0 18px 38px rgba(37, 99, 235, 0.42)',
                  },
                  '&:disabled': {
                    background: '#ccc'
                  }
                }}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
              >
                {loading ? t("Logging in...") : t("Login")}
              </Button>
              <LanguageSelector login />
            </form>
          </CardContent>
        </Paper>
      </Box>
    </Box>
  );
}

export default Login;
